import { Button, Flex, Spacer, useToast, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  CreateMasterClassInputDto,
  MasterClassFieldsFragment,
  MasterClassRelationAccessLinksFragment,
  MasterClassRelationClientsFragment,
  MasterClassRelationPatientCasesFragment,
  UpdateMasterClassInputDto,
  useCreateMasterClassMutation,
  useUpdateMasterClassMutation,
} from '@/@generated/codegen/API';
import { useLoggedInUserAuthContext } from '@/components/AuthTokenContextProvider/UserAuthContext';

import { CreateMasterClassFormColumnOne } from './components/CreateMasterClassFormColumnOne';
import {
  CreateMasterClassFormData,
  createMasterClassFormSchema,
} from './createMasterClassFormSchema';

type Props = {
  masterClass?: MasterClassFieldsFragment &
    MasterClassRelationAccessLinksFragment &
    MasterClassRelationClientsFragment &
    MasterClassRelationPatientCasesFragment;
  isDuplicating: boolean;
  closeForm: () => void;
};

export const CreateMasterClassForm = ({ masterClass, isDuplicating, closeForm }: Props) => {
  const { t } = useTranslation();
  const { userID } = useLoggedInUserAuthContext();
  // const { userAccessList } = useMasterClassUserAccessForMasterClass(masterClass?.id);
  const [_, setFirstColumnValid] = useState<boolean>(false);
  const [mutationCreateMasterClass] = useCreateMasterClassMutation();
  const [mutationUpdateMasterClass] = useUpdateMasterClassMutation();

  const toast = useToast();
  const form = useForm<CreateMasterClassFormData>({
    resolver: zodResolver(createMasterClassFormSchema),
    defaultValues: {
      title: masterClass?.title || '',
      patientCaseIDs: masterClass?.patientCases.edges.map((el) => el.node.id) || [],
      clientIDs: masterClass?.clients.edges.map((el) => el.node.id) || [],
    },
  });
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = form;

  const onSubmit = useCallback(
    async (values: CreateMasterClassFormData) => {
      if (values.clientIDs.length === 0) {
        toast({
          title: 'Error',
          description: t('admin.createMasterClass.form.masterClassClients.live.error'),
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
        return;
      }
      if (values.patientCaseIDs?.length === 0) {
        toast({
          title: 'Error',
          description: t('admin.createMasterClass.form.masterClassPatientCases.live.error'),
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
        return;
      }

      if (!masterClass || isDuplicating) {
        const masterClassInput: CreateMasterClassInputDto = {
          title: values.title,
          clientIds: values.clientIDs,
          patientCaseIds: values.patientCaseIDs,
        };
        await mutationCreateMasterClass({
          variables: {
            data: masterClassInput,
          },
        }).finally(() => {
          closeForm();
        });
      } else {
        const masterClassUpdate: UpdateMasterClassInputDto = {
          id: masterClass.id,
          title: values.title,
          clientIds: values.clientIDs,
        };
        await mutationUpdateMasterClass({
          variables: {
            data: masterClassUpdate,
          },
        }).finally(() => {
          closeForm();
        });
      }
    },
    [closeForm, masterClass, t, toast, isDuplicating]
  );

  return (
    <form
      onSubmit={(e) => {
        void handleSubmit(onSubmit)(e);
      }}
    >
      <fieldset disabled={isSubmitting}>
        <Flex justifyContent="space-between" overflowY="auto" overflowX="hidden" padding={1}>
          <VStack gap={2} w="full" maxW="300px">
            <CreateMasterClassFormColumnOne
              form={form}
              masterClass={masterClass}
              setFirstColumnValid={setFirstColumnValid}
              isDuplicating={isDuplicating}
            />
          </VStack>
        </Flex>

        <Spacer h={4} />

        <Button
          type="submit"
          p="2"
          width="full"
          background="blue.400"
          color="white"
          variant="solid"
          isDisabled={isSubmitting}
          isLoading={isSubmitting}
          aria-label="Submit form"
        >
          {masterClass && isDuplicating && t('common.duplicate')}
          {masterClass && !isDuplicating && t('common.update')}
          {!masterClass && t('common.create')}
        </Button>
      </fieldset>
    </form>
  );
};
